import { BaseModal, Loading } from "@src/common/components";
import { SIZE, ROLE } from "baseui/modal";
import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { datePickerDateToISODate } from "@src/common/utils/timezoneConverters";
import { createOrderEditSchema } from "../../utils/orderEditSchema";
import useEditOrderFormSubmit from "../../hooks/useEditOrderFormSubmit";
import EditOrderSlide from "./EditOrderSlide";
import {
  DispatcherOrderEntryAdditionalField,
  MultiStopsOptimizationType,
  Stop,
} from "@api/graphql/generated/generated-types";
import { shouldOpenConfirmRoute, useStopOrderEditing } from "@src/shipments/features/stopReordering";
import {
  useEditOrderShipment,
  EditOrderShipment,
} from "./hooks/useEditOrderShipment";
import ChangeRouteSlide from "./ChangeRouteSlide";
import { findIfShipmentIsMultiDay } from "@src/common/lib/shipmentStateFinders";
import { GetOneOffOrderDefaultValues } from "@src/orders/create/utils/formInitialStates";
import { initializeFormValues } from "@src/shipments/utils/makeFormDefaultValues";
import {
  isServiceTypeRequired,
  isVehicleTypeRequired,
  isCallerRequired,
} from "@src/common/lib/requiredAdditionalFieldUtils";
import { useAuth } from "@src/common/hooks/useAuth";

export enum EditOrderModalSlides {
  EDIT_ORDER = "EDIT_ORDER",
  CHANGE_ROUTE = "CHANGE_ROUTE",
}

interface EditOrderModalInnerProps {
  setIsModalOpen: (value: boolean) => void;
  isModalOpen: boolean;
  onConfirm: () => void;
  shipment: EditOrderShipment;
  refetchShipment: () => Promise<unknown>;
}

function EditOrderModal({
  setIsModalOpen,
  isModalOpen,
  onConfirm,
  shipment,
  refetchShipment,
}: EditOrderModalInnerProps) {
  const { reset, watch } = useFormContext();
  const { courier } = useAuth();
  const shouldKeepOrderEntrySequence: boolean =
    courier?.courier?.multiStopsOptimizationType ===
    MultiStopsOptimizationType.KeepOrderEntrySequence;
  const [stopsToCreate, setStopsToCreate] = useState<Stop[]>([]);
  const [selectedDuration, setSelectedDuration] = useState("noStopDates");
  const [currentSlide, setCurrentSlide] = useState<EditOrderModalSlides>(
    EditOrderModalSlides.EDIT_ORDER
  );
  const [isDifferentStopDate, setIsDifferentStopDate] = useState(false);
  const [isSubmissionPending, setIsSubmissionPending] = useState(false);
  const { setStopIdsToDelete } = useEditOrderFormSubmit({
    onConfirm,
    reset,
    setCurrentSlide,
    isDifferentStopDate,
    shipment,
  });
  const [shouldChangeRoute, setShouldChangeRoute] = useState(false);
  const watchStops = watch("stops");
  const {
    stopOrderEditing,
    stopOrderHint,
    hasStopOrderChange,
    recordBaselineStopOrder,
    clearStopOrderHint,
  } = useStopOrderEditing({
    shipment,
    shouldKeepOrderEntrySequence,
    shipmentType: watch("type"),
    formStops: watchStops,
  });
  useEffect(() => {
    onStopDateChange();
  }, [selectedDuration]);

  useEffect(() => {
    if (watchStops) {
      const stopsToCreate = watchStops.filter((stop: Stop) => !stop.id);
      setStopsToCreate(stopsToCreate);
    }
  }, [watchStops]);

  useEffect(() => {
    setShouldChangeRoute(
      shouldOpenConfirmRoute({
        hasDriver: !!shipment?.driver,
        status: shipment?.status,
        hasNewStop: !!stopsToCreate && stopsToCreate.length > 0,
        hasStopDateChange: isDifferentStopDate,
        hasPendingStopOrderChange: hasStopOrderChange,
      })
    );
  }, [stopsToCreate, shipment, isDifferentStopDate, hasStopOrderChange]);

  useEffect(() => {
    if (shipment) {
      const initialFormValues = initializeFormValues(
        shipment,
        shouldKeepOrderEntrySequence
      );
      const isMultiShipment = findIfShipmentIsMultiDay(
        shipment.stops,
        shipment.shipmentDate
      );
      reset(initialFormValues);
      recordBaselineStopOrder(initialFormValues.stops);
      isMultiShipment
        ? setSelectedDuration("hasStopDates")
        : setSelectedDuration("noStopDates");
    }
  }, [shipment]);

  const onStopDateChange = () => {
    if (selectedDuration === "hasStopDates" && shipment && watchStops?.length > 0) {
      const stopDateChanged = watchStops.some((stop) => {
        const stopDate = datePickerDateToISODate(stop.stopDate);
        const selectedShipmentStopDate = stop.id
          ? shipment.stops?.find((shipmentStop) => shipmentStop.id === stop.id)
              ?.stopDate
          : undefined;
        return stopDate !== selectedShipmentStopDate;
      });
      setIsDifferentStopDate(stopDateChanged);
    } else {
      setIsDifferentStopDate(false);
    }
  };

  const handleClose = () => {
    if (isSubmissionPending) return;
    setIsModalOpen(false);
    reset();
    setStopIdsToDelete([]);
    setIsDifferentStopDate(false);
    setCurrentSlide(EditOrderModalSlides.EDIT_ORDER);
    setShouldChangeRoute(false);
    clearStopOrderHint();
  };

  return (
    <BaseModal
      closeable={!isSubmissionPending}
      onClose={handleClose}
      isOpen={isModalOpen}
      animate
      autoFocus
      size={SIZE.auto}
      role={ROLE.dialog}
      focusLock={false}
    >
      {currentSlide === EditOrderModalSlides.EDIT_ORDER && (
        <EditOrderSlide
          onConfirm={onConfirm}
          setIsModalOpen={setIsModalOpen}
          shouldChangeRoute={shouldChangeRoute}
          setCurrentSlide={setCurrentSlide}
          selectedDuration={selectedDuration}
          setSelectedDuration={setSelectedDuration}
          onStopDateChange={onStopDateChange}
          isDifferentStopDate={isDifferentStopDate}
          shipment={shipment}
          stopOrderEditing={stopOrderEditing}
          refetchShipment={refetchShipment}
          onSubmittingChange={setIsSubmissionPending}
        />
      )}
      {currentSlide === EditOrderModalSlides.CHANGE_ROUTE && (
        <ChangeRouteSlide
          onCancel={handleClose}
          onConfirm={() => {
            reset();
            setStopIdsToDelete([]);
            clearStopOrderHint();
            setCurrentSlide(EditOrderModalSlides.EDIT_ORDER);
            onConfirm();
          }}
          shipment={shipment}
          stopOrderHint={stopOrderHint}
        />
      )}
    </BaseModal>
  );
}

interface Props {
  shipmentId: string;
  setIsModalOpen: (value: boolean) => void;
  isModalOpen: boolean;
  onConfirm: () => void;
}

function EditOrderModalWrapper({
  shipmentId,
  setIsModalOpen,
  isModalOpen,
  onConfirm,
}: Props) {
  const { courier, courierId } = useAuth();

  const { shipment, loading, isInitialLoading, error, refetch } =
    useEditOrderShipment({
      shipmentId,
      courierId: courierId ?? undefined,
      skip: !isModalOpen,
    });

  const dispatcherOrderEntryAdditionalFields: DispatcherOrderEntryAdditionalField[] =
    shipment?.order.endCustomer?.dispatcherOrderEntryAdditionalFields ??
    courier?.courier?.dispatcherOrderEntryAdditionalFields ??
    [];

  const serviceTypeRequired = isServiceTypeRequired(
    dispatcherOrderEntryAdditionalFields
  );

  const vehicleTypeRequired = isVehicleTypeRequired(
    dispatcherOrderEntryAdditionalFields
  );

  const callerRequired = isCallerRequired(dispatcherOrderEntryAdditionalFields);

  const formMethods = useForm<any>({
    resolver: yupResolver(
      createOrderEditSchema(
        serviceTypeRequired,
        vehicleTypeRequired,
        callerRequired
      )
    ),
    defaultValues: GetOneOffOrderDefaultValues(),
  });

  if (!isModalOpen) return null;

  // A failed refetch keeps the loaded order, so the open form stays for a retry.
  if (!shipment && (error || !loading)) {
    return (
      <BaseModal
        closeable
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        animate
        autoFocus
        size={SIZE.auto}
        role={ROLE.dialog}
      >
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-sm text-gray-700 dark:text-gray-200">
          <span>Failed to load order.</span>
          <span className="text-xs text-gray-500">
            {error?.message ?? "Shipment not found."}
          </span>
        </div>
      </BaseModal>
    );
  }

  // Only the first load shows the spinner. A refetch (e.g. after saving a new
  // stop) must leave the open form and slide alone.
  if (isInitialLoading || !shipment) {
    return (
      <BaseModal
        closeable
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        animate
        autoFocus
        size={SIZE.auto}
        role={ROLE.dialog}
      >
        <div className="flex items-center justify-center p-12">
          <Loading loading />
        </div>
      </BaseModal>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <EditOrderModal
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
        onConfirm={onConfirm}
        shipment={shipment}
        refetchShipment={refetch}
      />
    </FormProvider>
  );
}

export default EditOrderModalWrapper;
